import {isObject} from './object';

/**
 * @param {asyncFunction} func async function
 * @throws rethrows exceptions throwen by the async function
 * @example
 * import {asyncMain} from 'js-utils';
 * asyncMain(async () => {
 *   // your main async program code
 *   await doAsyncThings();
 * });
 */
export function asyncMain(func) {
	func().catch(e => {
		setTimeout(() => {
			throw e;
		}, 0);
	});
}

/**
 * Converts Node-style-callback async function to Promise returning one.
 * @param {function} cbFunc a Node-style-callback async function
 * @return {function} Promise returning async function
 * @example
 * import {toAsync} from 'js-utils';
 * function myCbFunc(a, b, cb) {
 *   return cb(null, a + b);
 * }
 *
 * const myAsyncFunc = toAsync(myCbFunc);
 * const res = await myAsyncFunc(1, 2);
 * console.log(res); // 3
 */
export function toAsync(cbFunc) {
	return (...args) =>
		new Promise((resolve, reject) =>
			this::cbFunc(...args, (err, res) => err ? reject(err) : resolve(res))
		);
}

/**
 * Calls a node-style-callback async function and returns a Promise.
 * @async
 * @param {function|string} func node-style-callback async function or name
 * of a function in *this*
 * @param {...*} args function arguments
 * @return {Promise<*>}
 * @throws {TypeError}
 * @example
 * import {callNodeAsync} from 'js-utils';
 * function myCbFunc(a, b, cb) {
 *   return cb(null, `${a + b} - ${this}`);
 * }
 *
 * // simple function call
 * const res1 = await callNodeAsync(myCbFunc, 1, 2));
 * console.log(res1); // "3 - undefined"
 *
 * // simple function call with this
 * const res2 = await "foo"::callNodeAsync(myCbFunc, 1, 2));
 * console.log(res2); // "3 - foo"
 *
 * // method call
 * const myObj = {myCbFunc, toString() {return 'im an object';}};
 * const res3 = await myObj::callNodeAsync("myCbFunc", 1, 2));
 * console.log(res3); // "3 - im an object"
 */
export function callNodeAsync(func, ...args) {
	return new Promise((resolve, reject) => {
		const funcType = typeof func;
		let cbFunc;
		if (funcType === 'function') {
			cbFunc = func;
		} else if (funcType === 'string' && this::isObject() && func in this) {
			cbFunc = this[func];
		} else {
			return reject(
				new TypeError('unsupported function type or func not in this object')
			);
		}

		return this::cbFunc(...args, (err, res) => err ? reject(err) : resolve(res));
	});
}

/**
 * Sleep for *ms* milliseconds.
 * @async
 * @param {number} ms time to sleep in milliseconds
 * @example
 * import {sleep} from 'js-utils';
 * await sleep(100); // "waits" for 100ms
 * console.log('foo'); // and prints "foo"
 */
export function sleep(ms) {
	return new Promise(resolve =>	setTimeout(resolve, ms));
}
